import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Loading } from '@bumble/app/components/Loading';
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface RouterPeerTestProps {
  cpeId: string;
  close: any;
}

interface RouterPeerTestResponse {
  sse_id: string;
  status: string;
}

export const RouterPeerTest = ({ cpeId, close }: RouterPeerTestProps) => {
  const api = useApi();

  const [peerRegions, setPeerRegions] = useState<string[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [isTesting, setIsTesting] = useState<boolean>(false);

  const { isLoading, data: regionsData } = useQuery(
    ['preparePeerRegion'],
    api.getPeerRegionsApiV1GetPeerRegionsGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    if (regionsData != null && regionsData != undefined) {
      setPeerRegions(regionsData.data || []);
    }
  }, [regionsData]);

  const testPeerRegion = useMutation(api.startCpePerformanceTestApiV1StartCpePerformanceTestPost, {
    onSuccess(data) {
      const resp = data.data as RouterPeerTestResponse;
      const msg = resp.status ?? `Tested Peer region: ${selectedRegion} successfully.`;
      toast.success(msg);
      setIsTesting(false);
      setTimeout(() => { close(cpeId, resp.sse_id); }, 3000);
    },
    onError: (error) => {
      apiError(error);
      setIsTesting(false);
    },
  });

  const handleChange = ({ target: { value }}: SelectChangeEvent): void => {
    setSelectedRegion(value);
  };

  const handleSubmit = () => {
    if (selectedRegion) {
      testPeerRegion.mutate({
        cpe_id: cpeId,
        region: selectedRegion
      });
      setIsTesting(true);
    } else {
      toast.error('Select Peer region');
    }
  };

  if (isLoading) return <Loading />

  return (
    <Box pt={2}>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="peer-region-select-label">Peer region</InputLabel>
          <Select
            labelId="peer-region-select-label"
            value={selectedRegion}
            label="Peer region"
            onChange={handleChange}
          >
            { peerRegions && peerRegions.map((item, i) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        {isTesting && <CircularProgress sx={{ mr: 5 }}/> }
        <Button variant="outlined" onClick={handleSubmit} disabled={!selectedRegion || isTesting}>
          Start Test
        </Button>
      </Box>
    </Box>
  );
};
